// src/App.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { onAuthChange } from "./services/firebase";
import { getUserProfile } from "./services/firebase";

import Auth        from "./components/Auth";
import Onboarding  from "./components/Onboarding";
import Layout      from "./components/Layout";
import Dashboard   from "./components/Dashboard";
import WeightChart from "./components/WeightChart";
import FoodTracker from "./components/FoodTracker";
import ExerciseLog from "./components/ExerciseLog";
import MealPlanner from "./components/MealPlanner";
import MealSuggestion    from "./components/MealSuggestion";
import GroceryList       from "./components/GroceryList";
import BodyFatProjection from "./components/BodyFatProjection";

// ─── Auth + Profile Context ───────────────────────────────────────────────────
export const AppContext = createContext(null);

export const useApp = () => {
  const ctx = useContext(AppContext);
  if (!ctx) throw new Error("useApp must be used inside AppContext.Provider");
  return ctx;
};

// ─── Protected route wrapper ──────────────────────────────────────────────────
const RequireAuth = ({ children }) => {
  const { user, loading } = useApp();
  if (loading) return <FullScreenLoader />;
  if (!user)   return <Navigate to="/auth" replace />;
  return children;
};

const RequireProfile = ({ children }) => {
  const { user, profile, profileError, loading, refreshProfile } = useApp();
  if (loading)   return <FullScreenLoader />;
  if (!user)     return <Navigate to="/auth" replace />;

  // Profile fetch failed — show error + retry instead of silently redirecting
  if (profileError) return (
    <div className="min-h-screen bg-[#0d1117] flex items-center justify-center px-4">
      <div className="card max-w-md w-full text-center">
        <div className="text-red-400 text-lg font-semibold mb-2">Failed to load profile</div>
        <p className="text-slate-400 text-sm mb-4">
          There was an issue connecting to the server. Please check your internet connection and try again.
        </p>
        <button onClick={() => refreshProfile()} className="btn-primary">
          Retry
        </button>
      </div>
    </div>
  );

  if (!profile)  return <Navigate to="/onboarding" replace />;
  return children;
};

const FullScreenLoader = () => (
  <div className="min-h-screen bg-[#0d1117] flex items-center justify-center">
    <div className="text-center animate-fade-in">
      <div className="text-[#ff7c0a] text-4xl font-bold mb-2"
           style={{ fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: "0.1em" }}>
        CUTTRACKER
      </div>
      <div className="spinner mx-auto mt-4" />
    </div>
  </div>
);

// ─── App ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [user,         setUser]         = useState(null);
  const [profile,      setProfile]      = useState(null);
  const [profileError, setProfileError] = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [lockedMeals,  setLockedMeals]  = useState([]);

  // Reload profile helper — called after onboarding or profile edits
  const refreshProfile = async (uid) => {
    const { data, error } = await getUserProfile(uid || user?.uid);
    if (error) { setProfileError(error); return null; }
    setProfileError(null);
    setProfile(data);
    return data;
  };

  useEffect(() => {
    const unsub = onAuthChange(async (firebaseUser) => {
      setUser(firebaseUser);
      if (firebaseUser) {
        const { data, error } = await getUserProfile(firebaseUser.uid);
        if (error) { setProfileError(error); }
        else       { setProfileError(null); setProfile(data); }
      } else {
        setProfile(null);
        setProfileError(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const ctx = { user, profile, setProfile, profileError, loading, refreshProfile, lockedMeals, setLockedMeals };

  return (
    <AppContext.Provider value={ctx}>
      <BrowserRouter>
        <Routes>
          {/* Public */}
          <Route path="/auth" element={
            loading ? <FullScreenLoader /> :
            user ? <Navigate to="/" replace /> : <Auth />
          } />

          {/* Onboarding (auth required, no profile yet) */}
          <Route path="/onboarding" element={
            <RequireAuth>
              <Onboarding />
            </RequireAuth>
          } />

          {/* Main app shell — all routes below require auth + profile */}
          <Route path="/" element={
            <RequireProfile>
              <Layout />
            </RequireProfile>
          }>
            <Route index            element={<Dashboard />} />
            <Route path="weight"    element={<WeightChart />} />
            <Route path="food"      element={<FoodTracker />} />
            <Route path="exercise"  element={<ExerciseLog />} />
            <Route path="meals"     element={<MealPlanner />} />
            <Route path="suggest"   element={<MealSuggestion />} />
            <Route path="grocery"   element={<GroceryList />} />
            <Route path="body-fat"  element={<BodyFatProjection />} />
            <Route path="*"         element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
    </AppContext.Provider>
  );
}
